import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Switch, Space, ConfigProvider, theme, Select, Button as AntdButton } from "antd";
import { Input } from "@/components/ui/inputForm";
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { getAllServicos, getServicoById, saveServico } from "@/services/servico";
import { message } from "antd";
import { tipos } from "../Cadastro";
import { usePermissions } from "@/hooks/usePermissions";

export default function ConsultaServico() {
  const [servicos, setServicos] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const navigate = useNavigate();
  const { canInsert, canEdit } = usePermissions();

  useEffect(() => {
    getAllServicos().then((res) => {
      if (res.status === 200) {
        setServicos(res.data.map((s: any) => ({ ...s, ativo: s.ativo ?? true })));
      }
    });
  }, []);

  const handleAdd = useCallback(() => {
    navigate("/servico/cadastro");
  }, [navigate]);

  const handleEdit = (id: string) => {
    navigate(`/servico/${id}`);
  };

  const toggleAtivo = async (id: string) => {
    try {
      // 1. Get the full service object
      const { data: servico } = await getServicoById(id);

      // 2. Change the 'ativo' status
      const updatedServico = { ...servico, ativo: !servico.ativo };

      // 3. Save the updated service
      const response = await saveServico(updatedServico);
      
      if (response.status === 200 || response.status === 201) {
        // 4. Update the local state to reflect the change
        setServicos((prev) =>
          prev.map((s) => (s.id === id ? { ...s, ativo: !s.ativo } : s))
        );
        message.success(`Serviço ${!servico.ativo ? 'ativado' : 'inativado'} com sucesso!`);
      } else {
        message.error("Falha ao atualizar o status do serviço.");
      }
    } catch (error) {
      message.error("Erro ao comunicar com o servidor.");
    }
  };

  const filteredServicos = servicos.filter((s) => {
    const matchesSearch = s.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "todos" ||
      (statusFilter === "ativos" && s.ativo) ||
      (statusFilter === "inativos" && !s.ativo);
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      render: (value: number) => value != null ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "-",
    },
    {
      title: "Valor Desconto",
      dataIndex: "valorDesconto",
      key: "valorDesconto",
      render: (value: number) => value != null ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "-",
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      render: (value: string) => {
        const tipo = tipos.find((t: { value: string; label: string }) => t.value === value);
        return tipo ? tipo.label : value;
      },
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (value: boolean, record: any) => (
        <Switch checked={!!value} onChange={() => toggleAtivo(record.id)} checkedChildren="Ativo" unCheckedChildren="Inativo" />
      ),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_: any, record: any) => (
        <Space size="middle">
          {canEdit(11) && (
            <AntdButton type="text" icon={<EditOutlined />} onClick={() => handleEdit(record.id)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Serviços</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-end mb-8">
          <Input
            type="text"
            placeholder="Pesquisar por nome"
            className="w-80"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={[
              { value: "todos", label: "Todos" },
              { value: "ativos", label: "Ativos" },
              { value: "inativos", label: "Inativos" },
            ]}
          />
          {canInsert(11) && (
            <AntdButton className="flex gap-2" onClick={handleAdd}>
              Novo
              <PlusOutlined />
            </AntdButton>
          )}
        </div>
        <div className="grid">
          <div>
            <Table columns={columns} dataSource={filteredServicos} rowKey="id" />
          </div>
        </div>
      </ConfigProvider>
    </>
  );
} 